import { useEffect, useMemo, useState, type FormEvent } from 'react';
import adminService from '@wlb/shared/api/admin/adminService';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../../components/ui/Button';
import { DetailList, type DetailRow } from '../../components/ui/DetailList';
import { FormError } from '../../components/ui/FormError';
import { StatusPill } from '../../components/ui/StatusPill';
import { formatDateTime } from '../../utils/format';
import {
  changedUserFields,
  toUserFormState,
  useUserDetail,
  type UserFormState,
} from './userForm';
import '../../components/ui/ui.css';
import '../../pages/pages.css';

/**
 * Activation state: whether the account is live, blocked, verified, and what
 * kind of seat it holds.
 *
 * Kept apart from the profile fields on purpose — these four switches are the
 * ones that lock a member out, and they should not ride along with a typo fix to
 * a surname.
 */
export function UserActivationTab() {
  const { t } = useTranslation('admin');
  const { user, reload } = useUserDetail();
  const [form, setForm] = useState<UserFormState>(() => toUserFormState(user));
  const [initial, setInitial] = useState<UserFormState>(() => toUserFormState(user));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const next = toUserFormState(user);
    setForm(next);
    setInitial(next);
  }, [user]);

  const update = <K extends keyof UserFormState>(key: K, value: UserFormState[K]) => {
    setForm(current => ({ ...current, [key]: value }));
  };

  const payload = changedUserFields(initial, form);
  const hasChanges = Object.keys(payload).length > 0;

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasChanges) {
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await adminService.updateUser(user.userGuid, payload);
      ToastService.show({ type: 'success', message: t('users.toasts.updated') });
      setInitial(form);
      reload();
    } catch (cause) {
      setSaveError(getUserFacingErrorMessage(cause, t('common.errorTitle')));
    } finally {
      setSaving(false);
    }
  };

  const statusRows = useMemo<readonly DetailRow[]>(
    () => [
      {
        label: t('users.details.fields.accountStatus'),
        value: user.isUserBlocked ? (
          <StatusPill label={t('status.blocked')} tone="danger" />
        ) : (
          <StatusPill
            label={user.isActive ? t('status.active') : t('status.inactive')}
            tone={user.isActive ? 'success' : 'neutral'}
          />
        ),
      },
      {
        label: t('users.details.fields.emailStatus'),
        value: (
          <StatusPill
            label={user.isEmailVerified ? t('status.verified') : t('status.unverified')}
            tone={user.isEmailVerified ? 'info' : 'warning'}
          />
        ),
      },
      {
        label: t('users.details.fields.role'),
        value: user.role ? t(`roles.${user.role}`) : t('roles.unknown'),
      },
      {
        label: t('users.details.fields.readOnlyAccess'),
        value: user.hasReadOnlyAccess ? t('common.yes') : t('common.no'),
      },
      { label: t('users.details.fields.joined'), value: formatDateTime(user.joiningDate) },
      { label: t('users.details.fields.updated'), value: formatDateTime(user.updatedOn) },
    ],
    [t, user],
  );

  return (
    <div className="page__section">
      <section className="page__panel" aria-labelledby="user-activation-state-heading">
        <h2 className="ui-section-title" id="user-activation-state-heading">
          {t('users.details.sections.activationState')}
        </h2>
        <DetailList rows={statusRows} />
      </section>

      <form className="page__panel" onSubmit={onSubmit} noValidate>
        <h2 className="ui-section-title">{t('users.details.sections.activationEdit')}</h2>
        <p className="page__note">{t('users.details.activationHint')}</p>
        <FormError variant="banner" message={saveError} />
        <div className="page__form-grid">
          <label className="ui-checkbox">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={event => update('isActive', event.target.checked)}
            />
            {t('users.details.isActive')}
          </label>
          <label className="ui-checkbox">
            <input
              type="checkbox"
              checked={form.isUserBlocked}
              onChange={event => update('isUserBlocked', event.target.checked)}
            />
            {t('users.details.isUserBlocked')}
          </label>
          <label className="ui-checkbox">
            <input
              type="checkbox"
              checked={form.isEmailVerified}
              onChange={event => update('isEmailVerified', event.target.checked)}
            />
            {t('users.details.isEmailVerified')}
          </label>
          <label className="ui-checkbox">
            <input
              type="checkbox"
              checked={form.makeAdmin}
              onChange={event => update('makeAdmin', event.target.checked)}
            />
            {t('users.details.makeAdmin')}
          </label>
        </div>
        <div className="page__panel-actions">
          <Button type="submit" disabled={saving || !hasChanges}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
